import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldOff, ArrowLeft, Mail, Phone, AlertCircle } from 'lucide-react';

const AccountDeactivated: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const email = searchParams.get('email') ?? '';

    return (
        <div className="min-h-screen flex bg-background">
            {/* Left panel — Branding (same as Login) */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900">
                <div className="absolute inset-0 opacity-10">
                    <div
                        className="absolute top-0 left-0 w-full h-full"
                        style={{
                            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(99, 102, 241, 0.3) 0%, transparent 50%),
                                             radial-gradient(circle at 75% 75%, rgba(99, 102, 241, 0.2) 0%, transparent 50%)`,
                        }}
                    />
                </div>
                <div className="absolute top-20 left-16 w-32 h-32 border border-indigo-500/20 rounded-2xl rotate-12 animate-pulse" />
                <div className="absolute bottom-32 right-20 w-24 h-24 border border-indigo-400/15 rounded-xl -rotate-12" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 border border-indigo-500/10 rounded-full" />

                <div className="relative z-10 flex flex-col justify-center px-16">
                    <div className="mb-8">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                                <span className="text-indigo-300 font-bold text-sm">3R</span>
                            </div>
                            <span className="text-white/80 font-semibold text-lg">PruneStone</span>
                        </div>
                        <h1 className="text-4xl font-bold text-white leading-tight mb-4">
                            Sistema de gestión<br />
                            <span className="text-indigo-300">empresarial</span>
                        </h1>
                        <p className="text-slate-400 text-lg leading-relaxed max-w-sm">
                            Plataforma integral para la administración de proyectos, ventas y finanzas.
                        </p>
                    </div>
                </div>
            </div>

            {/* Right panel */}
            <div className="flex flex-col justify-center w-full lg:w-1/2 px-6 sm:px-12 lg:px-20 py-12">
                <div className="w-full max-w-md mx-auto">
                    {/* Logo mobile */}
                    <div className="flex items-center gap-2 mb-10 lg:hidden">
                        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10">
                            <span className="text-primary font-bold">3R</span>
                        </div>
                        <h1 className="text-2xl font-bold text-foreground">PruneStone</h1>
                    </div>

                    {/* Icon */}
                    <div className="flex items-center justify-center w-20 h-20 mx-auto mb-6 rounded-2xl bg-red-100 dark:bg-red-950/30">
                        <ShieldOff className="w-10 h-10 text-red-600 dark:text-red-400" />
                    </div>

                    {/* Title */}
                    <div className="text-center mb-8">
                        <h2 className="text-2xl font-bold text-foreground mb-2">Cuenta Desactivada</h2>
                        <p className="text-muted-foreground leading-relaxed">
                            Tu cuenta ha sido desactivada por un administrador del sistema y no podés acceder al ERP en este momento.
                        </p>
                    </div>

                    {/* Email info */}
                    {email && (
                        <div className="flex items-center gap-3 p-4 rounded-xl bg-muted/50 border border-border mb-6">
                            <AlertCircle className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">
                                <span className="font-medium text-foreground">Cuenta:</span>{' '}
                                {email}
                            </p>
                        </div>
                    )}

                    {/* Steps to reactivate */}
                    <div className="rounded-xl border border-border overflow-hidden mb-8">
                        <div className="px-4 py-3 bg-muted/40 border-b border-border">
                            <p className="text-sm font-semibold text-foreground">¿Cómo reactivar tu cuenta?</p>
                        </div>
                        <div className="divide-y divide-border">
                            <div className="flex items-start gap-3 px-4 py-3.5">
                                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                                <p className="text-sm text-muted-foreground">
                                    Comunicate con el <span className="font-medium text-foreground">administrador del sistema</span> de tu empresa.
                                </p>
                            </div>
                            <div className="flex items-start gap-3 px-4 py-3.5">
                                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-bold flex-shrink-0 mt-0.5">2</span>
                                <p className="text-sm text-muted-foreground">
                                    Solicitá la <span className="font-medium text-foreground">reactivación de tu cuenta</span> indicando tu email.
                                </p>
                            </div>
                            <div className="flex items-start gap-3 px-4 py-3.5">
                                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-bold flex-shrink-0 mt-0.5">3</span>
                                <p className="text-sm text-muted-foreground">
                                    Una vez que el administrador active tu cuenta, podrás ingresar normalmente.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Contact channels */}
                    <div className="grid grid-cols-2 gap-3 mb-8">
                        <div className="flex items-center gap-3 p-3.5 rounded-xl border border-border bg-card">
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex-shrink-0">
                                <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <p className="text-xs font-medium text-foreground">Email</p>
                                <p className="text-xs text-muted-foreground">al administrador</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 p-3.5 rounded-xl border border-border bg-card">
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex-shrink-0">
                                <Phone className="w-4 h-4 text-green-600 dark:text-green-400" />
                            </div>
                            <div>
                                <p className="text-xs font-medium text-foreground">Teléfono</p>
                                <p className="text-xs text-muted-foreground">contacto directo</p>
                            </div>
                        </div>
                    </div>

                    {/* Back to login */}
                    <button
                        onClick={() => navigate('/login')}
                        className="w-full flex items-center justify-center gap-2 py-3 px-4 text-sm font-semibold text-foreground bg-muted hover:bg-muted/80 rounded-xl transition-all duration-200"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Volver al inicio de sesión
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AccountDeactivated;
